import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Users, Sparkles } from "lucide-react";

/**
 * Compact discovery widget for the Feed sidebar / empty state.
 * Shows up to 4 suggested users + 2 suggested communities. One-tap follow.
 */
export default function DiscoveryWidget() {
  const [users, setUsers] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [followed, setFollowed] = useState({});

  useEffect(() => {
    api.get("/discovery/suggested-users?limit=4").then((r) => setUsers(r.data || [])).catch(() => {});
    api.get("/discovery/suggested-communities?limit=2").then((r) => setCommunities(r.data || [])).catch(() => {});
  }, []);

  const follow = async (id) => {
    if (followed[id]) return;
    setFollowed((p) => ({ ...p, [id]: true }));
    try { await api.post(`/users/${id}/follow`); } catch { setFollowed((p) => ({ ...p, [id]: false })); }
  };

  if (users.length === 0 && communities.length === 0) return null;

  return (
    <aside data-testid="discovery-widget" className="brutal-card bg-card p-4 space-y-4">
      {users.length > 0 && (
        <div>
          <h3 className="text-sm uppercase tracking-wider font-bold mb-2 flex items-center gap-1">
            <Users size={14} strokeWidth={2.5} /> Suggested for you
          </h3>
          <ul className="space-y-2">
            {users.map((u) => (
              <li key={u.id} className="flex items-center gap-2" data-testid={`discovery-user-${u.username}`}>
                <Link to={`/u/${u.username}`} className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-80">
                  <Avatar user={u} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{u.display_name}</div>
                    <div className="text-xs text-muted-foreground truncate">@{u.username}</div>
                  </div>
                </Link>
                <button
                  onClick={() => follow(u.id)}
                  data-testid={`discovery-follow-${u.username}`}
                  className={`brutal-btn-sm px-2 py-1 text-xs ${followed[u.id] ? "bg-accent" : "bg-background"}`}
                >
                  {followed[u.id] ? "Following" : "Follow"}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {communities.length > 0 && (
        <div>
          <h3 className="text-sm uppercase tracking-wider font-bold mb-2 flex items-center gap-1">
            <Sparkles size={14} strokeWidth={2.5} /> Join a community
          </h3>
          <ul className="space-y-2">
            {communities.map((c) => (
              <li key={c.id} data-testid={`discovery-community-${c.slug || c.id}`}>
                <Link to={`/c/${c.slug || c.id}`} className="block hover:opacity-80">
                  <div className="text-sm font-semibold truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.member_count} member{c.member_count === 1 ? "" : "s"}</div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
}

function Avatar({ user }) {
  if (user.avatar_path) {
    const backend = process.env.REACT_APP_BACKEND_URL;
    return <img src={`${backend}/uploads/${user.avatar_path}`} alt="" className="w-8 h-8 rounded-full object-cover" />;
  }
  const initial = (user.display_name || user.username || "?")[0].toUpperCase();
  return <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-xs font-bold">{initial}</div>;
}
